import React, { FC, useMemo } from 'react';
import { Button, PlusIcon, Asset, Modal } from '@clearcover/atlas';
import {
  GetPaymentsSummaryByTermNumberResponse,
  TermStatusType,
} from '@clearcover/core-app-types';

import { env } from 'Shared/constants/env';

import { Card, CardError, CardHeader } from 'Shared/components';
import { AddPaymentMethodForm } from '../AddPaymentMethodForm';
import { MakeOneTimePaymentForm } from '../PaymentForm';
import styles from './styles.module.css';

import classNames from 'classnames';
import { AxiosError, AxiosPromise } from 'axios';
import { EditBillingInfo } from './EditBillingInfo';
import { useModalHashChange } from 'Shared/hooks';
import { MakeRenewalPaymentForm } from '../PaymentForm/MakeRenewalPaymentForm';

import { managePaymentInformationPolicy } from 'Admin/authPolicies';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { CardSectionInfoPairsType } from 'Shared/components/Card/CardSectionInfoPairs/CardSectionInfoPairs';
import { usePolicyContext } from 'Policies/utils';
import { createBillingSummaryDataPairs } from './utils';
import { EditPaymentsAndPayPlanSection } from './EditPaymentsAndPayPlanSection';

export type PolicyPaymentAndBillingType = {
  billingInfoReponse?: GetPaymentsSummaryByTermNumberResponse;
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
  error?: AxiosError;
  termId: string;
  loading: boolean;
  policyStatus: TermStatusType | undefined;
};

/**
 * Payments & Billing Card
 */
export const PolicyPaymentAndBilling: FC<PolicyPaymentAndBillingType> = ({
  billingInfoReponse,
  fetchBillingData,
  loading,
  error,
  policyStatus,
}) => {
  const {
    details: { isPolicyDraft, isRenewalDraft, timeZone },
    systemOfRecord,
    resourceEffectiveGrants,
  } = usePolicyContext();

  const { modalHashChange } = useModalHashChange('editBillingInfo');
  const downpaymentInstallment =
    billingInfoReponse?.billingInfo?.installments.find(
      (installment) => installment.description.toLowerCase() === 'downpayment'
    ) ?? [];

  const canEditPaymentInfo =
    systemOfRecord === 'clearcover' &&
    managePaymentInformationPolicy(resourceEffectiveGrants);
  // policyStatus is undefined until the policy request resolves, which is a
  // different request from the billing summary behind this card's `loading`.
  const isPolicyPendingOrUnknown =
    policyStatus === undefined || policyStatus === 'pending';

  const { canEditPayPlan, payPlanTooltipMessage } = useMemo(() => {
    // Checked before the rules below so the pending message wins over the
    // balance/full-pay messages, and an unknown status stays quiet.
    if (isPolicyPendingOrUnknown) {
      return {
        canEditPayPlan: false,
        payPlanTooltipMessage:
          policyStatus === 'pending'
            ? 'Pay plan cannot be modified while the policy is pending'
            : '',
      };
    }

    if (!billingInfoReponse?.billingInfo) {
      return { canEditPayPlan: false, payPlanTooltipMessage: '' };
    }

    const { version, overdue, currentBalance } = billingInfoReponse.billingInfo;

    // Rule 1: isRenewalDraft - LEGACY or PAYMENT FLEXIBILITY
    if (isRenewalDraft) {
      return { canEditPayPlan: true, payPlanTooltipMessage: '' };
    }

    // Rule 2: billingInfoResponse.version === 'v3' AND is not overdue  PAYMENT FLEXIBILITY AND is active term AND current balance is not 0
    if (
      version === 'v3' &&
      (overdue ?? 0) <= 0 &&
      !isRenewalDraft &&
      billingInfoReponse.billingInfo.installmentInterval !== 'FULL_PAY' &&
      (currentBalance ?? 0) !== 0
    ) {
      return { canEditPayPlan: true, payPlanTooltipMessage: '' };
    }

    if (
      version === 'v3' &&
      (overdue ?? 0) > 0 &&
      !isRenewalDraft &&
      billingInfoReponse.billingInfo.installmentInterval !== 'FULL_PAY'
    ) {
      return {
        canEditPayPlan: false,
        payPlanTooltipMessage:
          'Pay plan cannot be modified with overdue balance',
      };
    }

    if (
      version === 'v3' &&
      (overdue ?? 0) <= 0 &&
      !isRenewalDraft &&
      billingInfoReponse.billingInfo.installmentInterval === 'FULL_PAY'
    ) {
      return {
        canEditPayPlan: false,
        payPlanTooltipMessage:
          'Full pay customers can only edit their plan at renewal',
      };
    }

    if (version === 'v3' && !isRenewalDraft && (currentBalance ?? 0) === 0) {
      return {
        canEditPayPlan: false,
        payPlanTooltipMessage:
          'Pay plans cannot be modified when term balance is zero',
      };
    }

    return { canEditPayPlan: false, payPlanTooltipMessage: '' };
  }, [
    billingInfoReponse?.billingInfo,
    isRenewalDraft,
    isPolicyPendingOrUnknown,
    policyStatus,
  ]);

  const billingDataSections: {
    paymentMethodSection: Pick<CardSectionInfoPairsType, 'headers' | 'data'>;
    paymentInfoSection: Pick<CardSectionInfoPairsType, 'headers' | 'data'>;
  } = useMemo(
    () =>
      createBillingSummaryDataPairs(billingInfoReponse?.billingInfo, timeZone),
    [billingInfoReponse, timeZone]
  );

  if (error) {
    return (
      <Card>
        <CardHeader title={'Payments & Billing'}></CardHeader>
        <CardError error={error} refetch={fetchBillingData} />
      </Card>
    );
  }

  return (
    <Card id={loading ? '' : 'payment-billing'} className={styles.card}>
      <CardHeader title={'Payments & Billing'}>
        {(isRenewalDraft || !isPolicyDraft) &&
          canEditPaymentInfo &&
          !env.paymentsAndBillingNewDesign && (
            <Modal
              onVisibilityChange={modalHashChange}
              variant="full"
              toggle={(openModal) => (
                <Button
                  className={styles.editbillingButton}
                  size="sm"
                  variant="secondary"
                  onClick={openModal}
                  disabled={isPolicyPendingOrUnknown}
                  data-testid="open-edit-billing-modal"
                >
                  <span>Edit</span>
                </Button>
              )}
            >
              {() => (
                <EditBillingInfo
                  billingInfo={billingInfoReponse?.billingInfo}
                  bindDate={billingInfoReponse?.bindDate}
                  loading={loading}
                  fetchBillingData={fetchBillingData}
                  isRenewalDraft={isRenewalDraft}
                />
              )}
            </Modal>
          )}
      </CardHeader>

      <CardSectionInfoPairs
        className={styles.cardSection}
        headers={billingDataSections.paymentInfoSection.headers}
        data={billingDataSections.paymentInfoSection.data}
        loading={loading}
        dataFontStyle="none"
      />
      <CardSectionInfoPairs
        className={classNames(styles.cardSection, styles.last)}
        headers={billingDataSections.paymentMethodSection.headers}
        data={billingDataSections.paymentMethodSection.data}
        loading={loading}
        dataFontStyle="none"
      />

      {env.paymentsAndBillingNewDesign && (
        <EditPaymentsAndPayPlanSection
          billingInfo={billingInfoReponse?.billingInfo}
          bindDate={billingInfoReponse?.bindDate}
          loading={loading}
          fetchBillingData={fetchBillingData}
          isRenewalDraft={isRenewalDraft}
          canEditPayPlan={canEditPayPlan}
          payPlanTooltipMessage={payPlanTooltipMessage}
        />
      )}

      <div className={classNames(styles.actionButton)}>
        {canEditPaymentInfo && (
          <Modal
            variant="full"
            data-testid="billing-add-payment-modal"
            toggle={(open) => (
              <Button
                block
                variant="primary"
                onClick={open}
                data-testid="billing-make-payment"
              >
                Make Payment
              </Button>
            )}
          >
            {(close) =>
              isRenewalDraft && downpaymentInstallment ? (
                <MakeRenewalPaymentForm close={close} />
              ) : (
                <MakeOneTimePaymentForm
                  close={close}
                  fetchBillingData={fetchBillingData}
                />
              )
            }
          </Modal>
        )}
      </div>

      {!env.paymentsAndBillingNewDesign && (
        <div className={styles.actionButton}>
          {canEditPaymentInfo && (
            <Modal
              variant="full"
              data-testid="billing-add-payment-modal"
              toggle={(open) => (
                <Button
                  block
                  variant="secondary"
                  onClick={open}
                  data-testid="billing-add-payment-button"
                >
                  <Asset
                    icon={PlusIcon}
                    description="Add payment method"
                    height={12}
                    width={12}
                    className={styles.addIcon}
                  />
                  Add new payment method
                </Button>
              )}
            >
              {(close) => (
                <AddPaymentMethodForm
                  fetchBillingData={fetchBillingData}
                  isProcessOne={
                    billingInfoReponse?.billingInfo?.paymentMethods.some(
                      (method) => method.paymentProcessor === 'PROCESS_ONE'
                    ) ?? false
                  }
                  hasAdyenPaymentMethod={
                    billingInfoReponse?.billingInfo?.paymentMethods.some(
                      (method) => method.paymentProcessor === 'ADYEN'
                    ) ?? false
                  }
                  invoiceId={billingInfoReponse?.billingInfo?.invoiceId}
                  close={close}
                />
              )}
            </Modal>
          )}
        </div>
      )}
    </Card>
  );
};
